/* eslint-disable sonarjs/prefer-single-boolean-return */
import * as React from 'react';

import { List } from '@common/typescript/objects/List';
import { Nullable } from '@common/typescript/objects/Nullable';

import { useRequest } from '@app/hooks/useRequest';
import { AvailableStoreEntry } from '@app/objects/StoreEntry';
import { PriceType } from '@app/objects/Price';
import { ServiceType } from '@app/objects/Pet';

interface UrnRequestParams {
	crematoryId?: number;
	clinicId?: number;
	priceType?: PriceType;
	serviceType?: ServiceType;

	/* Filtering */
	filters: {
		name: string,
	};

	/* Pagination */
	count?: number;
	offset?: number;
}

/// This is what Provider expects to receive via props
type ContextRequestProps = Omit<UrnRequestParams, 'filters'>;

interface ProviderProps extends ContextRequestProps {
}

type OwnProps = React.PropsWithChildren<ProviderProps>;

function toParams(props: ProviderProps): ContextRequestProps {
	return {
		crematoryId: props.crematoryId,
		clinicId: props.clinicId,
		priceType: props.priceType,
		serviceType: props.serviceType,
	};
}

interface RawUrnDetails {
	params: ContextRequestProps;
	total: number;
}

function equals(a: ContextRequestProps, b: ContextRequestProps): boolean {
	if (a.crematoryId !== b.crematoryId) return false;
	if (a.clinicId !== b.clinicId) return false;
	if (a.priceType !== b.priceType) return false;
	if (a.serviceType !== b.serviceType) return false;

	return true;
}

export interface UrnList {
	total: number;
	items: {
		count: number;
		list: Array<AvailableStoreEntry>;
	};
}

export interface UrnContext {
	list: UrnList;
	setName: (name: string) => void;
}

const defaultContext: UrnContext = {
	list: {
		total: 0,
		items: {
			count: 0,
			list: [],
		},
	},
	setName: () => {
		throw new Error('Settings params with no context specified (UrnContext)');
	},
};

const Context = React.createContext<UrnContext>(defaultContext);

// TODO: use it in modal as well (currently modal uses its own requests)
function useUrns(params: ContextRequestProps, name: string): UrnList {
	const [raw, setRaw] = React.useState<Nullable<RawUrnDetails>>(null);
	const request = useRequest<List<AvailableStoreEntry>, UrnRequestParams>('availableStoreEntryList', undefined, { requestOnMount: false });

	React.useEffect(() => {
		if (params.serviceType === undefined) return;

		const data: UrnRequestParams = {
			...params,
			filters: {
				name,
			},
			count: 1, // Don't fetch data for now - just make sure that data exists
		};

		if (raw === null || !equals(raw.params, params)) {
			setRaw({
				params,
				total: 0,
			});
		}

		const updateTotal: boolean = !name;
		request
			.reload(data)
			.then((value: List<AvailableStoreEntry> | void) => {
				if (!updateTotal) return;
				if (!value) return;

				setRaw((q: Nullable<RawUrnDetails>) => {
					if (!q) return null;

					return {
						...q,
						total: value.count,
					};
				});
			});
	}, [params, name]);

	return React.useMemo(() => ({
		total: raw?.total ?? 0,
		items: {
			count: request.item?.count ?? 0,
			list: request.item?.list ?? [],
		},
	}), [raw?.total, request.item?.count, request.item?.list]);
}

export const UrnsProvider: React.FC<OwnProps> = (props: OwnProps) => {
	const [name, setName] = React.useState<string>('');
	const params = React.useMemo(() => toParams(props), [
		props.crematoryId,
		props.clinicId,
		props.priceType,
		props.serviceType,
		props.count,
		props.offset,
	]);
	const urns = useUrns(params, name);

	const context: UrnContext = React.useMemo(() => ({
		list: urns,
		setName,
	}), [urns, setName]);

	if (params.serviceType === undefined) return null;

	return (
		<Context.Provider value={context}>
			{props.children}
		</Context.Provider>
	);
};

export function useUrnsContext(): UrnContext {
	return React.useContext<UrnContext>(Context);
}
